import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

/**
 * Single source of truth for the View / Filter / Slice chip color tokens.
 *
 * These were previously duplicated between `filters/style.js` (PANEL_COLORS)
 * and `lhs/style.js` (COLORS). Both files now import CHIP_COLORS from here
 * for anything related to the View/Filter/Slice chip itself.
 */
export const CHIP_COLORS = {
  view: {
    primary: '#4f46e5',
    primaryLight: '#c7d2fe',
    bg: '#eef2ff',
    svgColor: '#4F46E5',
  },
  filter: {
    primary: '#102554',
    primaryLight: '#a5b4fc',
    bg: '#eef2ff',
    svgColor: '#102554',
  },
  slice: {
    primary: '#b62196',
    primaryLight: '#f5d0ea',
    bg: '#fdf2f8',
    svgColor: '#b62196',
  },
  neutral: {
    disabledText: '#636B73',
    disabledBg: 'rgba(228, 232, 242, 0.5)',
    disabledBorder: 'rgba(15, 23, 42, 0.05)',
    svgMuted: '#64748b',
  },
};

/**
 * Resolve the icon color for a chip given its type and whether it is
 * currently in a dimmed (muted/disabled) state.
 */
export const getChipIconColor = (type, isDimmed) => {
  const palette = CHIP_COLORS[type] || CHIP_COLORS.view;
  return isDimmed ? CHIP_COLORS.neutral.svgMuted : palette.svgColor;
};

/**
 * ChipBase renders both chip "flavors" used across the app:
 *  - variant="label"  -> the static section-header pill (filters.js: View / Filter / Slice)
 *  - variant="action" -> the interactive hover/quick-add pill (lhs.js: drag-drop action pills)
 *
 * States handled: normal, muted, disabled, hover (interactive), and non-interactive (legend/readonly).
 */
export const ChipBase = styled(Box, {
  shouldForwardProp: (prop) =>
    !['type', 'chipVariant', 'muted', 'disabled', 'interactive'].includes(prop),
})(({ type, chipVariant, muted, disabled, interactive }) => {
  const palette = CHIP_COLORS[type] || CHIP_COLORS.view;
  const isDimmed = Boolean(muted || disabled);

  const shared = {
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  if (chipVariant === 'action') {
    return {
      ...shared,
      gap: '6px',
      justifyContent: 'space-between',
      padding: '5px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 600,
      userSelect: 'none',
      transition: 'opacity 0.15s ease, transform 0.1s ease',
      cursor: disabled ? 'not-allowed' : interactive ? 'pointer' : 'default',
      pointerEvents: disabled ? 'none' : 'auto',
      backgroundColor: isDimmed ? CHIP_COLORS.neutral.disabledBg : palette.bg,
      border: isDimmed
        ? `0.667px solid ${CHIP_COLORS.neutral.disabledBorder}`
        : `1px solid ${palette.primaryLight}`,
      color: isDimmed ? CHIP_COLORS.neutral.disabledText : palette.primary,
      ...(interactive &&
        !disabled && {
          '&:hover': {
            opacity: 0.85,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }),
    };
  }

  // variant === 'label' (default): static section-header pill
  return {
    ...shared,
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontWeight: isDimmed ? 400 : 600,
    fontSize: '14px',
    lineHeight: '20px',
    backgroundColor: isDimmed ? CHIP_COLORS.neutral.disabledBg : palette.bg,
    border: isDimmed
      ? `0.667px solid ${CHIP_COLORS.neutral.disabledBorder}`
      : `0.667px solid ${palette.primaryLight}`,
    color: isDimmed ? CHIP_COLORS.neutral.disabledText : palette.primary,
  };
});
